import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { buildIcs, downloadIcs } from "@/lib/ics";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarPlus, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const REMINDER_OPTIONS = ["none", "1", "3", "7", "14"];

// Builds a minimal, privacy-safe default event title. User notes are never included.
function defaultTitle(deadline, matter, t) {
  const client = matter.client || matter.name || "";
  const parts = [];
  if (deadline.deadline_type === "Court hearing") {
    parts.push(t("cal.prefix_hearing"), client, matter.court_case_number);
  } else {
    parts.push(t("cal.prefix_deadline"), client, matter.court_case_number, deadline.description);
  }
  return parts.filter(Boolean).join(" — ");
}

export default function CalendarDialog({ open, onOpenChange, deadline, matter }) {
  const { t } = useT();
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("allday");
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [reminder, setReminder] = useState("none");

  useEffect(() => {
    if (!open || !deadline) return;
    setTitle(defaultTitle(deadline, matter, t));
    const isHearing = deadline.deadline_type === "Court hearing";
    setKind(isHearing ? "timed" : "allday");
    setStartTime(deadline.start_time || "10:00");
    setEndTime(deadline.end_time || "");
    setLocation(isHearing ? matter.court_authority || "" : "");
    setReminder("none");
    api.get("/settings")
      .then((r) => setReminder(r.data.calendar_default_reminder || "none"))
      .catch(() => {});
  }, [open, deadline, matter, t]);

  if (!deadline) return null;

  // Minimal description: matter reference info only — never notes/confidential content.
  const description = [matter.name, matter.internal_ref && `Ref: ${matter.internal_ref}`]
    .filter(Boolean)
    .join(" | ");

  const generate = () => {
    const allDay = kind === "allday";
    const ics = buildIcs({
      title,
      description,
      date: deadline.due_date,
      allDay,
      startTime: allDay ? null : startTime,
      endTime: allDay ? null : endTime || null,
      location: kind === "timed" ? location : "",
      reminderDays: reminder,
    });
    downloadIcs(`${title.replace(/[^\w\d-]+/g, "_").slice(0, 60)}.ics`, ics);
    toast.success(t("cal.downloaded"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="calendar-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{t("cal.add")}</DialogTitle>
          <DialogDescription>{t("cal.note")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="cal-title">{t("cal.title_label")}</Label>
            <Input
              id="cal-title"
              data-testid="calendar-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.date")}</Label>
              <div className="mt-1.5 text-sm text-slate-700 border border-slate-200 rounded-md px-3 py-2 bg-slate-50" data-testid="calendar-date-display">
                {deadline.due_date ? deadline.due_date.split("-").reverse().join(".") : ""}
              </div>
            </div>
            <div>
              <Label>{t("cal.kind_allday")} / {t("cal.kind_timed")}</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="calendar-kind-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="allday" data-testid="calendar-kind-allday">{t("cal.kind_allday")}</SelectItem>
                  <SelectItem value="timed" data-testid="calendar-kind-timed">{t("cal.kind_timed")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {kind === "timed" && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="cal-start">{t("form.start_time")}</Label>
                <Input
                  id="cal-start"
                  type="time"
                  data-testid="calendar-start-time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="cal-end">{t("form.end_time")}</Label>
                <Input
                  id="cal-end"
                  type="time"
                  data-testid="calendar-end-time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="cal-loc">{t("cal.location")}</Label>
                <Input
                  id="cal-loc"
                  data-testid="calendar-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}
          <div>
            <Label>{t("cal.reminder")}</Label>
            <Select value={reminder} onValueChange={setReminder}>
              <SelectTrigger data-testid="calendar-reminder-select" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REMINDER_OPTIONS.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`calendar-reminder-${r}`}>
                    {r === "none" ? t("cal.reminder_none") : r === "1" ? t("cal.reminder_day") : t("cal.reminder_days", { n: r })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" data-testid="calendar-cancel" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="button" variant="outline" data-testid="calendar-open-button" onClick={generate}>
              <CalendarClock className="h-4 w-4 mr-1.5" /> {t("cal.open")}
            </Button>
            <Button
              type="button"
              data-testid="calendar-add-button"
              onClick={generate}
              className="bg-slate-900 hover:bg-slate-800"
            >
              <CalendarPlus className="h-4 w-4 mr-1.5" /> {t("cal.add")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
